import UserProfileContext from '../../context/userprofilecontext.js';
import PageSkeleton from './pageskeleton.js';
import SankeyChart from './sankey-chart-component.js';
import { useContext, useState } from 'react';
import { useGetTransferAnalysis } from '../../api/transfer_analysis.js';
import DataFilter from './filter/filtercontext.js';
import DateRangeFilter from './filter/filterdaterange.js';
import MultiSelectFilter from './filter/filtermultiselect.js';
import {transformToSankeyData} from './sankey-utils.js';
import {TransactionViewerByID} from './TransactionViewer.js';
import ContentDialog from './ContentDialog.js';
import { Skeleton, Box } from '@mui/material';

// Format currency values for better display
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const transformSampleDataToSankeyData = (data) => {
    const sankeyData = transformToSankeyData(data, {
        getLinkSource: item => item.source,
        getLinkTarget: item => item.target,
        getLinkValue: item => item.value,
        getSourceNodeId: item => item.source,
        getTargetNodeId: item => item.target,
        getSourceNodeName: item => item.source,
        getTargetNodeName: item => item.target,
        getLinkLabel: item => `$${formatCurrency(item.value)}`,
        getNodeData: item => (item),
        getLinkData: item => (item)
    });
    return sankeyData;
}

export default function ReportTransferAnalysis(props) {
    const transferAnalysisResult = useGetTransferAnalysis();

    // Format values for display
    const formatNodeValue = (node) => formatCurrency(node.value);
    const formatLinkLabel = (link) => `$${formatCurrency(link.value)}`;

    const [filteredData, setFilteredData] = useState(
        transferAnalysisResult.data ? transformSampleDataToSankeyData(transferAnalysisResult.data) : null
    );

    // for the dialog
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedTransactions, setSelectedTransactions] = useState([]);
    const [dialogTitle, setDialogTitle] = useState("");

    const handleFilteredDataChange = (newFilteredData) => {
        setFilteredData(transformSampleDataToSankeyData(newFilteredData));
    };

    const handleLinkClick = (details) => {
        console.log("Link clicked:", details);
        const transactionIDs = details.transactionIDs || [];
        setSelectedTransactions(transactionIDs);
        setDialogTitle(`Transfers from ${details.source} to ${details.target}`);
        setDialogOpen(true);
    }

    const handleNodeClick = (details) => {
        console.log("Node clicked:", details);
        const transactionIDs = details.transactionIDs || [];
        setSelectedTransactions(transactionIDs);
        setDialogTitle(`Transactions contributing to ${details.name}`);
        setDialogOpen(true);
    }

    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    // Show loading skeleton while data is being fetched
    if (transferAnalysisResult.isLoading || !transferAnalysisResult.data) {
        return (
            <Box sx={{ width: '100%', px: 4 }}>
                {/* Filter skeleton */}
                <Box sx={{ mb: 3 }}>
                    <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1, mb: 2 }} />
                </Box>

                {/* Chart skeleton */}
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Box sx={{ width: '100%' }}>
                        <Skeleton variant="rectangular" height={800} sx={{ borderRadius: 2 }} />

                        {/* Additional detail skeletons for realism */}
                        <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'center' }}>
                            <Skeleton variant="circular" width={40} height={40} />
                            <Skeleton variant="circular" width={40} height={40} />
                            <Skeleton variant="circular" width={40} height={40} />
                        </Box>
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <>
            <Box sx={{ flexShrink: 0 }}>
                <DataFilter
                    data={transferAnalysisResult.data}
                    onFilteredDataChange={handleFilteredDataChange}
                    title="Filter Transactions"
                    defaultCollapsed={true}
                >
                    <DateRangeFilter
                        field="date"
                        label="Transaction Date"
                    />

                    <MultiSelectFilter
                        field="source"
                        label="Source"
                    />

                    <MultiSelectFilter
                        field="target"
                        label="Target"
                    />
                </DataFilter>
            </Box>

            {filteredData && filteredData.nodes && filteredData.links &&
             filteredData.nodes.length > 0 && filteredData.links.length > 0 && (
                <Box sx={{ flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                    <Box sx={{ width: '100%', px: 4 }}>
                        <SankeyChart
                            data={filteredData}
                            showLinkLabels={true}
                            highlightCycles={true}
                            cycleLinkColor="#FF3366"
                            cycleOpacity={0.9}
                            cycleStrokeWidth={4}
                            width={1400}
                            height={800}
                            margin={{ top: 40, right: 260, bottom: 40, left: 260 }}
                            wrapLabels={true}
                            formatNodeValue={formatNodeValue}
                            formatLinkLabel={formatLinkLabel}
                            nodePadding={35}
                            nodeWidth={25}
                            labelPadding={15}
                            colorPalette={[
                                "#1f77b4", "#2ca02c", "#9467bd", "#8c564b",
                                "#e377c2", "#7f7f7f", "#bcbd22", "#17becf", "#d62728"
                            ]}
                            onLinkClick={handleLinkClick}
                            onNodeClick={handleNodeClick}
                        />
                        <ContentDialog
                            open={dialogOpen}
                            onClose={handleCloseDialog}
                            title={dialogTitle}
                            fullScreen={true}
                        >
                            <TransactionViewerByID transactionIDs={selectedTransactions} />
                        </ContentDialog>
                    </Box>
                </Box>
            )}
        </>
    );
}